import { Component, Input, OnInit, OnDestroy, signal, inject, input, ChangeDetectionStrategy, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { Router } from '@angular/router';
import { GlobalStateService } from '../../../service/global.service';
import { LibraryService } from '../libraries/library.service';
import { ModalButtonIDs } from '../libraries/libraries-page-refactored.component';
import { EntityLayoutConfigService } from '../../services/entity-layout-config.service';
import { ProcessProgressView, ProgressIndicatorComponent, ShimmerVariant } from '@optima-stride-ai/optima-components';
import {
  VaicWhiteboardComponent
} from '@optima-stride-ai/optima-app-framework';
import { GetDefaultConfig, ModalDialogSizes, ModalDialogs } from '../../components/modal 2/modal';
import { OptimaDefaultModalService } from '../../components/modal 2/modal.service';
import { Subscription } from 'rxjs';


/**
 * Whiteboard/Whiteboard viewer component
 * Loads and displays whiteboard files using the lib-whiteboard component from the library
 */
@Component({
  selector: 'app-whiteboard-viewer',
  standalone: true,
  imports: [CommonModule, VaicWhiteboardComponent, ProgressIndicatorComponent],
  templateUrl: './whiteboard-viewer.component.html',
  styleUrls: ['./whiteboard-viewer.component.sass'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WhiteboardViewerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() id?: string;
  @Input() onBack?: () => void;
  /** Prepared iframe entity; regular-app callers retain the metadata lookup. */
  entity = input<IBizEntity>();
  @ViewChild('template') vaicWhiteboardComponent!: VaicWhiteboardComponent;
  readonly maxWhiteboardFileSizeMb = 1;
  readonly maxImageUploadSizeMb = 3;

  private readonly router = inject(Router);
  private readonly libraryService = inject(LibraryService);
  private readonly logger = inject(LoggerService);
  private readonly global = inject(GlobalStateService);
  private readonly entityLayoutConfigService = inject(EntityLayoutConfigService);
  private readonly optimaDefaultModalService = inject(OptimaDefaultModalService);

  // Component state
  isLoading = signal<boolean>(true);
  hasError = signal<boolean>(false);
  errorMessage = signal<string>('');
  whiteBoardEntity = signal<IBizEntity | null>(null);
  private saveRequestSubscription?: Subscription;

  onWhiteboardSave(): void {
    this.vaicWhiteboardComponent.saveCanvas()
  }
  ngOnInit(): void {

    this.saveRequestSubscription = this.global.saveRequested$.subscribe((payload) => {
      if (!payload) {
        return;
      }

      if (!payload.entityId || payload.entityId === this.id) {
        this.logger.info(`💾 Save requested from ${payload.source || 'unknown'} for whiteboard entity ${this.id || 'unknown'}`);
        this.onWhiteboardSave();
      }
    });

    this.global.setIsPreviewOpen(true);
    if (this.id) {
      this.loadWhiteboard();
    } else {
      this.logger.error('❌ No ID provided for whiteboard viewer');
      this.hasError.set(true);
      this.errorMessage.set('No file ID provided');
      this.isLoading.set(false);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['id'] && !changes['id'].firstChange) {
      this.loadWhiteboard();
    }
  }

  ngOnDestroy(): void {
    this.saveRequestSubscription?.unsubscribe();
    this.global.setIsPreviewOpen(false);
  }

  /**
   * Load whiteboard entity
   */
  private async loadWhiteboard(): Promise<void> {
    try {
      if (!this.id) {
        throw new Error('No ID provided');
      }

      this.isLoading.set(true);
      this.hasError.set(false);

      this.logger.info(`📐 Loading whiteboard file: ${this.id}`);

      // Fetch the entity using the library service
      const iframeEntity = this.entity();
      const fileMetadata = iframeEntity && String(iframeEntity.id) === String(this.id)
        ? iframeEntity
        : await this.libraryService.getBizEntityById(this.id);
      if (fileMetadata) {
        this.logger.info(`✅ Loaded whiteboard entity: ${fileMetadata.name}`);
        this.whiteBoardEntity.set(fileMetadata);
      } else {
        throw new Error('Failed to load file metadata');
      }

      this.isLoading.set(false);
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to load whiteboard file';
      this.logger.error(`❌ Error loading whiteboard: ${errorMsg}`);
      this.hasError.set(true);
      this.errorMessage.set(errorMsg);
      this.isLoading.set(false);
    }
  }
  // openConfirmationDialog(): void {
  //   const modalConfig = GetDefaultConfig();
  //   modalConfig.title = 'Confirmation Required';
  //   modalConfig.dialog = ModalDialogs.ConfirmationDialogue;
  //   modalConfig.size = ModalDialogSizes.LG;
  //   modalConfig.model = {
  //     in: {
  //       title: 'Leave Whiteboard?',
  //       message: 'Do you want to close the whiteboard and go back?',
  //       confirmButtonText: 'Proceed',
  //       cancelButtonText: 'Cancel',
  //       onConfirm: () => {
  //         if (this.onBack) {
  //           this.onBack();
  //           return;
  //         }

  //         this.router.navigate([]);
  //       }
  //     },
  //     out: "null"
  //   };

  //   this.optimaDefaultModalService.show(modalConfig);
  // }

openConfirmationDialog(): void {
    const modalConfig = GetDefaultConfig();
    modalConfig.title = 'Confirmation Required';
    modalConfig.dialog = ModalDialogs.ConfirmationDialogue;
    modalConfig.size = ModalDialogSizes.LG;
    modalConfig.model = {
      in: {
        message: 'Do you want to close the whiteboard and go back?'
      },
      out: "null"
    };

    modalConfig.footerButtons.push(
      {
        id: ModalButtonIDs.Message_Cancel,
        label: 'Cancel',
        color: 'secondary',
        click: () => {},
        getData: false,
        formValidate: false,
        closesModal: true,
      },
      {
        id: ModalButtonIDs.Message_OK,
        label: 'Proceed',
        color: 'primary',
        click: () => {
          if (this.onBack) {
            this.onBack();
            return;
          }

          this.router.navigate([]);
        },
        getData: false,
        formValidate: false,
        closesModal: true,
      }
    );
   this.optimaDefaultModalService.show(modalConfig);
  }
  /**
   * Handle whiteboard close event
   */
  onWhiteboardClose(): void {
    this.logger.info('🔙 Whiteboard closed');
          // if (this.onBack) {
          //   this.onBack();
          //   return;
          // }
    this.openConfirmationDialog();
  }

  /**
   * Handle whiteboard delete event wrapper
   */
  handleDelete(event: any): void {
    if (event && typeof event === 'object' && 'id' in event) {
      this.onWhiteboardDelete(event as IBizEntity);
    }
  }

  /**
   * Handle whiteboard delete event
   */
  onWhiteboardDelete(entity: IBizEntity): void {
    this.logger.info(`🗑️ Whiteboard delete requested for: ${entity.name}`);

    try {
      // Clear entity layout cache
      this.entityLayoutConfigService.clearEntityCache(entity.id);

      // Navigate back to parent
      this.router.navigate([]);

      this.logger.info(`✅ Whiteboard deleted and navigated away`);
    } catch (error) {
      this.logger.error(`❌ Error deleting whiteboard: ${error}`);
    }
  }
  readonly ProcessProgressView = ProcessProgressView;
  readonly ShimmerVariant = ShimmerVariant;
}
